import { BusinessBusiness } from "../types";
import { AudienceMode, AudienceEvidence } from "./audienceEvidenceTypes";

export interface AudienceActionButton {
  id: string;
  label: string;
  url: string;
  canonicalUrl: string;
  audienceMode: AudienceMode;
  pageType: string;
  isPrimary: boolean;
  isOfficial: boolean;
  isActionable: boolean;
  confidence: number;
  priorityRank: number;
  explanation: string;
  sourceTitle?: string;
  sourceType?: string;
  lastCheckedAt?: string;
  sourceDateFormatted?: string;
}

/**
  * Sanitizes, normalizes, and validates a raw URL.
  * Strips tracking params, normalizes double slashes, rejects unsafe protocols or search engine query pages.
  */
export function sanitizeAndValidateUrl(
  rawUrl: string | null | undefined,
  officialDomain?: string
): {
  url: string | null;
  canonicalUrl: string | null;
  isValid: boolean;
  isOfficial: boolean;
  rejectionReason?: string;
} {
  if (!rawUrl || typeof rawUrl !== "string") {
    return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "Empty or missing URL" };
  }

  const trimmed = rawUrl.trim();
  if (trimmed.length < 5) {
    return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "URL too short" };
  }

  // Protocol check
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("javascript:") || lower.startsWith("data:") || lower.startsWith("file:") || lower.startsWith("vbscript:")) {
    return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "Unsafe protocol" };
  }

  let prepended = trimmed;
  if (!lower.startsWith("http://") && !lower.startsWith("https://")) {
    prepended = `https://${trimmed}`;
  }

  try {
    const parsed = new URL(prepended);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "Invalid protocol" };
    }

    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, "");
    if (!hostname || !hostname.includes(".")) {
      return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "Invalid hostname" };
    }

    // Reject raw search engine result query URLs as official destination pages
    if ((hostname.includes("google.com") || hostname.includes("bing.com") || hostname.includes("yahoo.com") || hostname.includes("duckduckgo.com")) &&
        (parsed.pathname.includes("/search") || parsed.pathname.includes("/url"))) {
      return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "Search engine query wrapper URL" };
    }

    // Normalize path: fix double slashes in path
    let cleanPath = parsed.pathname.replace(/\/+/g, "/");
    if (cleanPath.length > 1 && cleanPath.endsWith("/")) {
      cleanPath = cleanPath.slice(0, -1);
    }

    // Strip tracking query parameters
    const trackingKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "msclkid", "_ga", "mc_eid"];
    trackingKeys.forEach(k => parsed.searchParams.delete(k));

    const cleanQuery = parsed.searchParams.toString();
    const finalUrl = `${parsed.protocol}//${parsed.host}${cleanPath}${cleanQuery ? `?${cleanQuery}` : ""}`;
    const canonicalUrl = `${hostname}${cleanPath}`.toLowerCase();

    // Check if domain matches official domain
    let isOfficial = false;
    if (officialDomain) {
      const cleanOfficial = officialDomain.toLowerCase().replace(/^www\./, "").trim();
      if (cleanOfficial && (hostname === cleanOfficial || hostname.endsWith(`.${cleanOfficial}`))) {
        isOfficial = true;
      }
    }

    return {
      url: finalUrl,
      canonicalUrl,
      isValid: true,
      isOfficial
    };
  } catch (err) {
    return { url: null, canonicalUrl: null, isValid: false, isOfficial: false, rejectionReason: "URL parse failure" };
  }
}

/**
 * Returns audience-specific user-facing button labels conforming strictly to requirements.
 */
export function getAudienceButtonLabel(
  audienceMode: AudienceMode,
  pageType: string,
  url: string,
  title?: string
): string {
  const textContext = `${pageType} ${title || ""} ${url}`.toLowerCase();

  switch (audienceMode) {
    case "job_seeker": {
      if (textContext.includes("apply") || textContext.includes("application")) return "Apply Now";
      if (textContext.includes("intern") || textContext.includes("student")) return "View Internship";
      if (textContext.includes("post") || textContext.includes("facebook") || textContext.includes("linkedin") || textContext.includes("social")) return "View Hiring Post";
      if (textContext.includes("contact") || textContext.includes("hr")) return "Contact About Jobs";
      if (textContext.includes("job") || textContext.includes("vacancy") || textContext.includes("opening")) return "View Open Jobs";
      return "View Careers";
    }

    case "recruiter": {
      if (textContext.includes("contact") || textContext.includes("talent") || textContext.includes("hr")) return "Find Hiring Contact";
      if (textContext.includes("team") || textContext.includes("leadership") || textContext.includes("management") || textContext.includes("about")) return "View Team";
      if (textContext.includes("expansion") || textContext.includes("growth") || textContext.includes("news")) return "View Growth Evidence";
      if (textContext.includes("linkedin.com")) return "Visit LinkedIn";
      if (textContext.includes("role") || textContext.includes("opening") || textContext.includes("vacancy")) return "View Open Roles";
      return "View Careers";
    }

    case "staffing_agency": {
      if (textContext.includes("hr") || textContext.includes("human resources")) return "Contact HR";
      if (textContext.includes("operations") || textContext.includes("manager") || textContext.includes("contact")) return "Contact Operations";
      if (textContext.includes("partner") || textContext.includes("vendor") || textContext.includes("supplier")) return "View Partnership Page";
      if (textContext.includes("campaign") || textContext.includes("announcement") || textContext.includes("press")) return "View Hiring Campaign";
      if (textContext.includes("role") || textContext.includes("openings") || textContext.includes("job")) return "View Open Roles";
      return "View Staffing Demand";
    }

    case "career_coach": {
      if (textContext.includes("entry") || textContext.includes("grad") || textContext.includes("junior")) return "View Entry-Level Roles";
      if (textContext.includes("intern") || textContext.includes("co-op")) return "View Internship Program";
      if (textContext.includes("train") || textContext.includes("learn") || textContext.includes("development") || textContext.includes("upskill")) return "View Training Program";
      if (textContext.includes("apply") || textContext.includes("process") || textContext.includes("how-to")) return "View Application Process";
      return "View Career Opportunities";
    }

    case "workforce_organization": {
      if (textContext.includes("community") || textContext.includes("partner")) return "View Community Partnership";
      if (textContext.includes("inclusive") || textContext.includes("diversity") || textContext.includes("veteran") || textContext.includes("second-chance")) return "View Inclusive Hiring";
      if (textContext.includes("train") || textContext.includes("apprentice") || textContext.includes("skill")) return "View Training Program";
      if (textContext.includes("contact")) return "Contact Partnership Team";
      return "View Workforce Program";
    }

    case "college_career_center": {
      if (textContext.includes("intern")) return "View Internships";
      if (textContext.includes("grad")) return "View Graduate Program";
      if (textContext.includes("campus") || textContext.includes("recruiting") || textContext.includes("fair")) return "View Campus Recruitment";
      if (textContext.includes("deadline") || textContext.includes("calendar")) return "View Application Deadlines";
      if (textContext.includes("contact")) return "Contact Early Careers Team";
      return "View Student Opportunities";
    }

    case "sales_professional": {
      if (textContext.includes("decision") || textContext.includes("exec") || textContext.includes("contact-us")) return "Find Decision Maker";
      if (textContext.includes("team") || textContext.includes("leadership") || textContext.includes("about")) return "View Leadership";
      if (textContext.includes("procure") || textContext.includes("vendor") || textContext.includes("supplier") || textContext.includes("rfp")) return "View Procurement";
      if (textContext.includes("expansion") || textContext.includes("location") || textContext.includes("news")) return "View Expansion News";
      if (textContext.includes("website") || textContext.includes("audit") || textContext.includes("opportunity")) return "View Website Opportunity";
      return "Visit Contact Page";
    }

    case "business_development": {
      if (textContext.includes("supplier") || textContext.includes("vendor") || textContext.includes("procure")) return "Become a Supplier";
      if (textContext.includes("franchise")) return "View Franchise Information";
      if (textContext.includes("expansion") || textContext.includes("growth") || textContext.includes("new-location")) return "View Expansion Plans";
      if (textContext.includes("contact") || textContext.includes("bizdev")) return "Contact Business Development";
      if (textContext.includes("inquir") || textContext.includes("form")) return "View Business Inquiries";
      return "View Partnership Opportunities";
    }

    case "agency_freelancer": {
      if (textContext.includes("contact")) return "Visit Contact Page";
      if (textContext.includes("marketing") || textContext.includes("media") || textContext.includes("press")) return "View Marketing Contact";
      if (textContext.includes("social") || textContext.includes("facebook") || textContext.includes("instagram") || textContext.includes("linkedin")) return "View Social Profiles";
      if (textContext.includes("partner") || textContext.includes("rfp") || textContext.includes("opportunity")) return "View Agency Opportunities";
      if (textContext.includes("audit") || textContext.includes("issue") || textContext.includes("redesign") || textContext.includes("tech")) return "Open Website Audit";
      return "View Website Issues";
    }

    case "business_explorer": {
      if (textContext.includes("menu")) return "View Menu";
      if (textContext.includes("book") || textContext.includes("reserve")) return "Book Now";
      if (textContext.includes("order") || textContext.includes("shop") || textContext.includes("store")) return "Order Online";
      if (textContext.includes("event") || textContext.includes("schedule")) return "View Events";
      if (textContext.includes("review") || textContext.includes("rating") || textContext.includes("google")) return "View Reviews";
      if (textContext.includes("photo") || textContext.includes("gallery")) return "View Photos";
      if (textContext.includes("facebook") || textContext.includes("instagram") || textContext.includes("social")) return "Visit Social Page";
      return "Visit Website";
    }

    default:
      return "Visit Page";
  }
}

/**
 * Priority rank computation for sorting buttons.
 * Lower rank = higher priority.
 */
function getButtonPriorityRank(
  isOfficial: boolean,
  pageType: string,
  isActionable: boolean,
  confidence: number
): number {
  if (isOfficial && isActionable && confidence >= 0.8) return 1; // Verified official page matching target action
  if (isOfficial && isActionable) return 2;                      // Verified official subpage
  if (pageType.includes("social")) return 3;                    // Official social post
  if (confidence >= 0.7) return 4;                              // Trusted third-party platform
  if (pageType.includes("contact")) return 5;                   // General contact page
  return 6;                                                     // Official homepage / fallback
}

/**
 * Builds actionable, ranked, deduplicated button sets for a business and audience mode.
 */
export function getAudienceActionButtons(
  business: BusinessBusiness,
  audienceMode: AudienceMode,
  verifiedEvidences?: any[]
): {
  primaryButtons: AudienceActionButton[];
  overflowButtons: AudienceActionButton[];
  allButtons: AudienceActionButton[];
} {
  const officialDomain = (business as any).officialDomain || (business.website ? sanitizeAndValidateUrl(business.website).canonicalUrl?.split("/")[0] : undefined);
  const candidates: AudienceActionButton[] = [];
  const seenCanonicals = new Set<string>();

  // 1. Process explicit verifiedEvidences if provided
  if (verifiedEvidences && verifiedEvidences.length > 0) {
    verifiedEvidences.forEach((ev, idx) => {
      const sanitized = sanitizeAndValidateUrl(ev.sourceUrl, officialDomain);
      if (sanitized.isValid && sanitized.url && sanitized.canonicalUrl && !seenCanonicals.has(sanitized.canonicalUrl)) {
        seenCanonicals.add(sanitized.canonicalUrl);
        const label = getAudienceButtonLabel(audienceMode, ev.evidenceType || ev.sourceType, sanitized.url, ev.sourceTitle);
        const isOfficial = sanitized.isOfficial || ev.sourceType.includes("official");
        const priorityRank = getButtonPriorityRank(isOfficial, ev.evidenceType || ev.sourceType, true, ev.confidence);

        candidates.push({
          id: `btn_ev_${ev.id || idx}`,
          label,
          url: sanitized.url,
          canonicalUrl: sanitized.canonicalUrl,
          audienceMode,
          pageType: ev.evidenceType || ev.sourceType,
          isPrimary: false,
          isOfficial,
          isActionable: true,
          confidence: ev.confidence,
          priorityRank,
          explanation: ev.explanation || `Verified ${audienceMode.replace(/_/g, " ")} page for ${business.name}`,
          sourceTitle: ev.sourceTitle,
          sourceType: ev.sourceType,
          lastCheckedAt: ev.lastCheckedAt,
          sourceDateFormatted: ev.sourcePublishedAt || ev.sourceUpdatedAt ? undefined : undefined
        });
      }
    });
  }

  // 2. Synthesize baseline buttons from business properties if missing
  const hiringPageUrl = business.hiringPageUrl || (business.websiteAudit as any)?.hiringPageUrl || (business as any).hiring_page_url;
  if (hiringPageUrl && ["job_seeker", "recruiter", "staffing_agency", "career_coach", "college_career_center"].includes(audienceMode)) {
    const sanitized = sanitizeAndValidateUrl(hiringPageUrl, officialDomain);
    if (sanitized.isValid && sanitized.url && sanitized.canonicalUrl && !seenCanonicals.has(sanitized.canonicalUrl)) {
      seenCanonicals.add(sanitized.canonicalUrl);
      const label = getAudienceButtonLabel(audienceMode, "careers_portal", sanitized.url, `${business.name} Careers`);
      candidates.push({
        id: `btn_base_careers_${business.id}`,
        label,
        url: sanitized.url,
        canonicalUrl: sanitized.canonicalUrl,
        audienceMode,
        pageType: "careers_portal",
        isPrimary: true,
        isOfficial: true,
        isActionable: true,
        confidence: 0.95,
        priorityRank: 1,
        explanation: `Official active careers portal for ${business.name}`,
        sourceTitle: `${business.name} Careers`
      });
    }
  }

  if (business.website) {
    const sanitized = sanitizeAndValidateUrl(business.website, officialDomain);
    if (sanitized.isValid && sanitized.url && sanitized.canonicalUrl && !seenCanonicals.has(sanitized.canonicalUrl)) {
      seenCanonicals.add(sanitized.canonicalUrl);
      const label = getAudienceButtonLabel(audienceMode, "official_website", sanitized.url, business.name);
      candidates.push({
        id: `btn_base_website_${business.id}`,
        label,
        url: sanitized.url,
        canonicalUrl: sanitized.canonicalUrl,
        audienceMode,
        pageType: "official_website",
        isPrimary: false,
        isOfficial: true,
        isActionable: true,
        confidence: 0.90,
        priorityRank: audienceMode === "business_explorer" ? 1 : 6,
        explanation: `Official website for ${business.name}`,
        sourceTitle: business.name
      });
    }
  }

  // Sort candidate buttons by priority rank (lower rank first) and confidence
  candidates.sort((a, b) => {
    if (a.priorityRank !== b.priorityRank) return a.priorityRank - b.priorityRank;
    return b.confidence - a.confidence;
  });

  // Assign primary status (Max 3 primary buttons)
  const primaryButtons = candidates.slice(0, 3).map((btn, idx) => ({ ...btn, isPrimary: true }));
  const overflowButtons = candidates.slice(3).map(btn => ({ ...btn, isPrimary: false }));
  const allButtons = [...primaryButtons, ...overflowButtons];

  return {
    primaryButtons,
    overflowButtons,
    allButtons
  };
}
